import { useEffect, useMemo, useState, useRef } from "react";
import { Loader2, Play, Square } from "lucide-react";
import { useTrail, useSpring, animated } from "@react-spring/web";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { SearchableSelect } from "@/components/organisms/studio/SearchableSelect";
import { compactStageRows, stageBadge, type StageStatus } from "@/components/organisms/studio/AgentRunProgress";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { useAgentRun, useDropdown } from "@/hooks/studio-platform";
import { useAgentRunStore } from "@/stores/agentRunStore";
import type { AgentRecord, AgentRunRecord } from "@/services/studio/types";
import { AgentRunReportView } from "./AgentRunReportView";
import { agentOutcome } from "@/lib/studio/agentOutcomes";
import { agentArtifactUrl } from "@/lib/studio/runs";
import { getMultiAgentAuthHeaders } from "@/services/multi-agent/client";
import { deriveTargetPolicy, scopeString } from "./agentRunPolicy";
import { useImproveInMultiAgent } from "@/hooks/multi-agent";

export type RunPhase = "configure" | "running" | "done";

type SelectOption = {
  value: string; label: string; id?: string; metadata_id?: string;
  metadata_item_id?: string; metadata_type?: string; full_name?: string;
  name?: string; file_name?: string;
};

function optionsWithoutRunAll(options: SelectOption[] | undefined) {
  return (options ?? []).filter((o) => o.value !== "__all__");
}


interface AgentRunTabProps {
  agent: AgentRecord;
  // When set, fetches this historical run and shows it in the done state
  viewRunId?: string | null;
  onViewRunLoaded?: () => void;
}

export function AgentRunTab({ agent, viewRunId, onViewRunLoaded }: AgentRunTabProps) {
  // SSE-driven state lives in the store so it survives route navigation
  const entry = useAgentRunStore((s) => s.runs.get(agent.id));
  const phase = entry?.phase ?? "configure";
  const events = entry?.events ?? [];
  const result = entry?.result ?? "";
  const runId = entry?.runId ?? null;
  const artifacts = entry?.artifacts ?? [];

  // Form fields stay local — no need to persist across navigation
  const [input, setInput] = useState(agent.goal || "");
  const [scope, setScope] = useState<Record<string, string>>({ mode: "org" });

  const persistedRunQ = useAgentRun(runId || undefined);
  // Historical run fetched when user clicks "View report" in Run History tab
  const historicalRunQ = useAgentRun(viewRunId || undefined);
  const artifactOrgId =
    persistedRunQ.data?.run.org_id ||
    historicalRunQ.data?.run.org_id ||
    agent.org_id || null;

  // Clear stale run state immediately when a new historical run is requested
  useEffect(() => {
    if (!viewRunId) return;
    useAgentRunStore
      .getState()
      .updateEntry(agent.id, { result: "", runId: null, artifacts: [], errorMessage: null });
  }, [viewRunId]);

  // Populate done state once historical run data arrives
  const loadedHistoricalId = useRef<string | null>(null);
  // Guard against reconnecting the same run's live stream twice (StrictMode / refetch)
  const reconnectedId = useRef<string | null>(null);
  useEffect(() => {
    if (!viewRunId || !historicalRunQ.data) return;
    if (loadedHistoricalId.current === viewRunId) return;
    loadedHistoricalId.current = viewRunId;
    const status = historicalRunQ.data.run.status;
    // Still-live run (e.g. opened via ?run_id after a browser refresh): reconnect to
    // the live replay SSE so steps/report/artifacts resume streaming, instead of
    // freezing on this one-shot snapshot in a fake "done" state.
    if ((status === "running" || status === "queued") && reconnectedId.current !== viewRunId) {
      reconnectedId.current = viewRunId;
      useAgentRunStore.getState().reconnectRun(agent.id, agent.name, viewRunId);
      onViewRunLoaded?.();
      return;
    }
    useAgentRunStore.getState().updateEntry(agent.id, {
      // Empty stays EMPTY. Seeding a "No report saved for this run." SENTENCE as
      // the result made a report-less failed run look like it had a report: the
      // genuine empty branch never fired, and the MD/PDF/Word + Improve CTAs
      // rendered for a run whose only content was that placeholder.
      result: historicalRunQ.data.run.result_md || "",
      runId: viewRunId,
      artifacts: historicalRunQ.data.artifacts,
      phase: "done",  // reach the done-view so old runs get the result layout + Improve button
      // This entry may still hold the live error of a DIFFERENT run the user just
      // watched fail; the record being viewed carries its own.
      errorMessage: null,
    });
    onViewRunLoaded?.();
  }, [historicalRunQ.data?.run.id, viewRunId]);

  // Derive displayed content — if data is cached it's available before the
  // effect fires, so read it directly to avoid an empty-state flash.
  const isLoadingHistoricalRun = Boolean(viewRunId && historicalRunQ.isLoading);
  const displayResult =
    result ||
    (viewRunId && historicalRunQ.data?.run.result_md
      ? historicalRunQ.data.run.result_md
      : "");

  // Why this run ended, for the case where there is no persisted record to read it
  // from — the run that JUST failed on this screen. The store keeps the terminal
  // `error` frame's message; the persisted record (once it loads) is authoritative.
  const liveErrorMessage = entry?.errorMessage ?? null;
  const fallbackRunError = historicalRunQ.data?.run.error_message ?? liveErrorMessage;
  // A run is only "done" if it actually is. Hardcoding it stamped a green DONE
  // badge on runs that had failed.
  const fallbackRunStatus =
    historicalRunQ.data?.run.status ?? (fallbackRunError ? "error" : "done");

  // Animate on every phase transition (configure ↔ running ↔ done ↔ configure)
  const [phaseSpring, phaseSpringApi] = useSpring(() => ({
    opacity: 1,
    transform: "translateY(0px)",
    config: { tension: 380, friction: 28 },
  }));
  const prevPhase = useRef(phase);
  useEffect(() => {
    if (prevPhase.current !== phase) {
      phaseSpringApi.start({
        from: { opacity: 0, transform: "translateY(6px)" },
        to: { opacity: 1, transform: "translateY(0px)" },
      });
      prevPhase.current = phase;
    }
  }, [phase]);

  const targetPolicy = useMemo(() => deriveTargetPolicy(agent), [agent.id]);
  const allowedMetadataTypes = targetPolicy.metadataTypes ?? [];
  const selectedMetadataType = scope.metadata_type || targetPolicy.metadataType || allowedMetadataTypes[0] || "";
  const apexType = selectedMetadataType === "ApexTrigger" ? "ApexTrigger" : "ApexClass";

  // All dropdowns — called unconditionally (rules of hooks)
  const metadataTypesQ = useDropdown("list_metadata_types", {}, ["apex", "metadata"].includes(targetPolicy.kind));
  const apexClassesQ = useDropdown("list_classes", {}, targetPolicy.kind === "apex" && apexType === "ApexClass");
  const apexTriggersQ = useDropdown("list_metadata", { metadata_type: "ApexTrigger" }, targetPolicy.kind === "apex" && apexType === "ApexTrigger");
  const objectsQ = useDropdown("list_objects", {}, targetPolicy.kind === "object");
  const fieldsQ = useDropdown("get_object_fields", { object_name: scope.metadata_name || "" }, targetPolicy.kind === "object" && Boolean(scope.metadata_name));
  const flowsQ = useDropdown("list_flows", {}, targetPolicy.kind === "flow");
  const permSetsQ = useDropdown("list_perm_sets", {}, targetPolicy.kind === "permission");
  const metadataItemsQ = useDropdown("list_metadata", { metadata_type: selectedMetadataType }, targetPolicy.kind === "metadata" && Boolean(selectedMetadataType));
  const usersQ = useDropdown("list_org_users", {}, targetPolicy.kind === "user");

  // Initialise scope from agent
  useEffect(() => {
    const policy = deriveTargetPolicy(agent);
    const types = policy.metadataTypes ?? [];
    const nextType =
      policy.kind === "apex"
        ? scopeString(agent.scope, "metadata_type") === "ApexTrigger" ? "ApexTrigger" : "ApexClass"
        : scopeString(agent.scope, "metadata_type") || policy.metadataType || types[0] || "";
    setScope({ mode: policy.kind === "org" ? "org" : "metadata_name", metadata_type: nextType, metadata_name: scopeString(agent.scope, "metadata_name"), field_name: scopeString(agent.scope, "field_name") });
    setInput(agent.goal || "");
  }, [agent.id]);

  const metadataTypeOptions = useMemo(() => {
    const opts = optionsWithoutRunAll(metadataTypesQ.data?.options);
    const allowed = new Set(allowedMetadataTypes);
    return allowed.size ? opts.filter((o) => allowed.has(o.value)) : opts;
  }, [allowedMetadataTypes, metadataTypesQ.data?.options]);

  const targetOptions = useMemo(() => {
    if (targetPolicy.kind === "apex") return optionsWithoutRunAll(apexType === "ApexTrigger" ? apexTriggersQ.data?.options : apexClassesQ.data?.options);
    if (targetPolicy.kind === "object") return optionsWithoutRunAll(objectsQ.data?.options);
    if (targetPolicy.kind === "flow") return optionsWithoutRunAll(flowsQ.data?.options);
    if (targetPolicy.kind === "permission") return optionsWithoutRunAll(permSetsQ.data?.options);
    if (targetPolicy.kind === "metadata") return optionsWithoutRunAll(metadataItemsQ.data?.options);
    if (targetPolicy.kind === "user") return optionsWithoutRunAll(usersQ.data?.options);
    return [];
  }, [apexClassesQ.data?.options, apexTriggersQ.data?.options, apexType, flowsQ.data?.options, metadataItemsQ.data?.options, objectsQ.data?.options, permSetsQ.data?.options, usersQ.data?.options, targetPolicy.kind]);

  const selectedTargetOption = useMemo(() => targetOptions.find((o) => o.value === scope.metadata_name), [scope.metadata_name, targetOptions]);

  const targetLoading =
    (["apex", "metadata"].includes(targetPolicy.kind) && metadataTypesQ.isLoading && metadataTypeOptions.length === 0) ||
    (targetPolicy.kind === "apex" && (apexType === "ApexTrigger" ? apexTriggersQ.isLoading : apexClassesQ.isLoading)) ||
    (targetPolicy.kind === "object" && objectsQ.isLoading) ||
    (targetPolicy.kind === "flow" && flowsQ.isLoading) ||
    (targetPolicy.kind === "permission" && permSetsQ.isLoading) ||
    (targetPolicy.kind === "metadata" && metadataItemsQ.isLoading) ||
    (targetPolicy.kind === "user" && usersQ.isLoading);

  useEffect(() => {
    if (scope.metadata_name && targetOptions.length > 0 && !targetOptions.some((o) => o.value === scope.metadata_name)) {
      setScope((p) => ({ ...p, metadata_name: "", field_name: "" }));
    }
  }, [scope.metadata_name, targetOptions]);

  const runScope = useMemo<Record<string, string>>(() => {
    if (targetPolicy.kind === "org") {
      const s: Record<string, string> = { mode: "org", run_all: "true", run_mode: "fast" };
      if (scope.metadata_type) s.metadata_type = scope.metadata_type;
      if (scope.metadata_name) {
        s.metadata_name = selectedTargetOption?.full_name || selectedTargetOption?.name || scope.metadata_name;
        s.target = s.metadata_name;
      }
      const mid = selectedTargetOption?.metadata_id || selectedTargetOption?.metadata_item_id || selectedTargetOption?.id;
      if (mid) { s.metadata_id = mid; s.metadata_item_id = mid; }
      if (scope.field_name) s.field_name = scope.field_name;
      return s;
    }
    const s: Record<string, string> = {
      mode: "metadata_name", run_mode: "fast",
      metadata_type: targetPolicy.kind === "apex" ? apexType : selectedMetadataType,
      metadata_name: selectedTargetOption?.full_name || selectedTargetOption?.name || scope.metadata_name || "",
    };
    const mid = selectedTargetOption?.metadata_id || selectedTargetOption?.metadata_item_id || selectedTargetOption?.id;
    if (mid) { s.metadata_id = mid; s.metadata_item_id = mid; }
    if (targetPolicy.kind === "object") s.object_name = s.metadata_name;
    if (targetPolicy.runAll && !scope.metadata_name) s.run_all = "true";
    if (scope.field_name) s.field_name = scope.field_name;
    return s;
  }, [apexType, scope.field_name, scope.metadata_name, selectedMetadataType, selectedTargetOption, targetPolicy.kind, targetPolicy.runAll]);

  // Sync persisted result when done (server may have fuller result_md after stream
  // ends). GUARD: persistedRunQ refetches every 30s — re-setting result/artifacts
  // on each refetch re-renders SkillResultMarkdown and RE-MOUNTS its Monaco editors,
  // which intermittently render blank (the "answer in Monaco disappeared" bug). Only
  // update when content actually changed.
  useEffect(() => {
    if (phase !== "done" || !persistedRunQ.data) return;
    const md = persistedRunQ.data.run.result_md;
    const arts = persistedRunQ.data.artifacts;
    const cur = useAgentRunStore.getState().runs.get(agent.id);
    const resultChanged = Boolean(md) && md !== cur?.result;
    const artsChanged = (arts?.length ?? 0) !== (cur?.artifacts?.length ?? 0);
    if (!resultChanged && !artsChanged) return;
    useAgentRunStore.getState().updateEntry(agent.id, {
      ...(resultChanged ? { result: md as string } : {}),
      ...(artsChanged ? { artifacts: arts } : {}),
    });
  }, [persistedRunQ.data, phase]);

  const missingRequiredTarget = targetPolicy.required && !runScope.metadata_name;
  const canRun = phase !== "running" && !missingRequiredTarget;

  const start = () => {
    if (!canRun) return;
    useAgentRunStore.getState().startRun(agent.id, agent.name, input || agent.goal, runScope);
  };

  const stop = () => {
    useAgentRunStore.getState().stopRun(agent.id);
  };

  const { handoff: handoffToMultiAgent, improving } = useImproveInMultiAgent();
  const improveInMultiAgent = () =>
    handoffToMultiAgent(async () => {
      const fetched: { name: string; content: string }[] = [];
      for (const a of artifacts.slice(0, 5)) {
        if (!runId) break;
        try {
          const res = await fetch(agentArtifactUrl(runId, a.name, artifactOrgId), {
            headers: getMultiAgentAuthHeaders(),
          });
          if (res.ok) fetched.push({ name: a.name, content: (await res.text()).slice(0, 15000) });
        } catch { /* skip artifact on fetch error */ }
      }
      return { source: "agent", agentName: agent.name, agentGoal: agent.goal, report: displayResult, artifacts: fetched, runId };
    });

  const outcome = agentOutcome(agent);

  // ── Target controls ─────────────────────────────────────────────────────────
  const renderTargetControls = () => {
    if (targetPolicy.kind === "org") {
      return (
        <div className="rounded-md border border-border bg-muted/20 px-4 py-3 text-sm">
          <p className="font-semibold">Runs org-wide from your active login.</p>
          <p className="mt-1 text-muted-foreground">{targetPolicy.help}</p>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {targetPolicy.runAll && !targetPolicy.required && (
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            Runs all matching metadata when no target is selected.
          </div>
        )}
        {(targetPolicy.kind === "apex" || targetPolicy.kind === "metadata") && (
          allowedMetadataTypes.length === 1 ? (
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">Metadata type</span>
              <Badge variant="outline" className="font-mono text-xs">{allowedMetadataTypes[0]}</Badge>
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="font-mono text-xs text-muted-foreground">Metadata type</label>
              <SearchableSelect
                value={selectedMetadataType}
                onChange={(value) => setScope((p) => ({ ...p, mode: "metadata_name", metadata_type: value, metadata_name: "", field_name: "" }))}
                options={metadataTypeOptions}
                placeholder={metadataTypesQ.isLoading ? "Loading…" : "Select metadata type"}
                emptyHint="No matching metadata type found."
                disabled={metadataTypesQ.isLoading && metadataTypeOptions.length === 0}
              />
            </div>
          )
        )}
        <div className="space-y-1.5">
          <label className="font-mono text-xs text-muted-foreground">{targetPolicy.targetLabel}</label>
          <SearchableSelect
            value={scope.metadata_name || ""}
            onChange={(value) => setScope((p) => ({ ...p, mode: "metadata_name", metadata_type: runScope.metadata_type, metadata_name: value, field_name: "" }))}
            options={targetOptions}
            placeholder={targetLoading ? "Loading…" : targetPolicy.placeholder}
            emptyHint={targetPolicy.emptyHint}
            disabled={targetLoading}
          />
        </div>
        {targetPolicy.kind === "object" && scope.metadata_name && (
          <div className="space-y-1.5">
            <label className="font-mono text-xs text-muted-foreground">Field (optional)</label>
            <SearchableSelect
              value={scope.field_name || ""}
              onChange={(value) => setScope((p) => ({ ...p, field_name: value }))}
              options={optionsWithoutRunAll(fieldsQ.data?.options)}
              placeholder={fieldsQ.isLoading ? "Loading…" : "All fields"}
              emptyHint="No field found."
              disabled={fieldsQ.isLoading}
            />
          </div>
        )}
        <p className="text-xs text-muted-foreground">{targetPolicy.help}</p>
      </div>
    );
  };

  // ── Configure phase ──────────────────────────────────────────────────────────
  if (phase === "configure") {
    return (
      <animated.div style={phaseSpring} className="flex-1 overflow-y-auto min-h-0">
        <div className="grid gap-6 p-5 xl:grid-cols-[1fr_22rem]">
          {/* Left: target + instruction + run button */}
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {targetPolicy.title}
                </p>
                <Badge variant="outline" className="text-[10px]">{targetPolicy.status}</Badge>
              </div>
              {renderTargetControls()}
            </div>

            <div className="space-y-2">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Instruction
              </p>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={agent.goal}
                className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {missingRequiredTarget && (
                <p className="text-xs text-muted-foreground">
                  Select a {targetPolicy.targetLabel?.toLowerCase()} before running.
                </p>
              )}
            </div>

            <Button type="button" variant="charcoal" size="lg" onClick={start} disabled={!canRun} className="w-full">
              <Play className="mr-2 h-4 w-4" />
              Run Agent
            </Button>
          </div>

          {/* Right: expected output — sticky */}
          <div className="sticky top-5 self-start space-y-3">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Expected Output
            </p>
            <Badge variant="outline">{outcome.title}</Badge>
            <div className="space-y-1.5">
              {outcome.bullets.map((b) => (
                <div key={b} className="rounded-md border border-border bg-muted/10 px-3 py-2 text-sm">{b}</div>
              ))}
            </div>
            {outcome.artifacts.length > 0 && (
              <div>
                <p className="mb-1.5 font-mono text-[10px] text-muted-foreground uppercase tracking-wide">Downloads after run</p>
                <div className="flex flex-wrap gap-1.5">
                  {outcome.artifacts.map((a) => (
                    <span key={a} className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </animated.div>
    );
  }

  // ── Running phase ────────────────────────────────────────────────────────────
  if (phase === "running") {
    const rows = compactStageRows(events);
    const hasReport = Boolean(result);

    return (
      <animated.div style={phaseSpring} className="flex flex-1 min-h-0 flex-col">
        {hasReport ? (
          // Report started — collapse steps to a dot bar, report fills the rest
          <>
            <RunningStepBar rows={rows} onStop={stop} />
            <div className="flex-1 overflow-y-auto p-5">
              <SkillResultMarkdown content={result} isStreaming={true} />
            </div>
          </>
        ) : (
          // No report yet — steps are the hero
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 pt-5 pb-4">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {rows.length > 0 ? `Execution Steps · ${rows.length}` : "Starting…"}
              </p>
            </div>
            <div className="px-5 pb-5">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <Loader2 className="mb-3 h-8 w-8 animate-spin text-muted-foreground/25" />
                  <p className="font-mono text-xs text-muted-foreground">Connecting…</p>
                </div>
              ) : (
                <StepCardsGrid rows={rows} />
              )}
            </div>
          </div>
        )}
      </animated.div>
    );
  }

  // ── Done phase ───────────────────────────────────────────────────────────────
  if (isLoadingHistoricalRun) {
    return (
      <animated.div style={phaseSpring} className="flex flex-1 flex-col items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/30" />
        <p className="font-mono text-xs text-muted-foreground">Loading report…</p>
      </animated.div>
    );
  }

  // Local UI for the tab: the shared AgentRunReportView (result + files, Weave CTA
  // on top, MD/PDF/Word export) — upstream's reconnect/hydration logic above is
  // preserved; only this render was swapped to the local report view.
  return (
    <animated.div style={phaseSpring} className="flex flex-1 min-h-0 min-w-0 flex-col overflow-y-auto p-5">
      <AgentRunReportView
        run={
          persistedRunQ.data?.run
            ? ({
                ...persistedRunQ.data.run,
                result_md: displayResult || persistedRunQ.data.run.result_md,
                // The record is authoritative, but it can load before the backend has
                // written the failure — fall back to what the stream reported.
                error_message: persistedRunQ.data.run.error_message ?? liveErrorMessage,
              } as AgentRunRecord)
            : ({
                id: runId ?? "",
                org_id: artifactOrgId ?? "",
                agent_id: agent.id,
                surface_name: agent.name,
                status: fallbackRunStatus,
                result_md: displayResult,
                error_message: fallbackRunError,
              } as unknown as AgentRunRecord)
        }
        artifacts={artifacts}
        steps={[]}
        usage={entry?.usage ?? null}
        improving={improving}
        onImprove={improveInMultiAgent}
      />
    </animated.div>
  );
}

// ── Step cards grid ───────────────────────────────────────────────────────────
type StageRow = { stage: string; tools: string[]; status: StageStatus; error?: string };

// Strip verbose metadata from stage labels (e.g. "LLM generating (10s) · claude-sonnet-4.6 · emit_artifact" → "LLM generating")
function shortStageLabel(stage: string): string {
  return stage.split(" · ")[0].trim();
}

function StepCardsGrid({ rows }: { rows: StageRow[] }) {
  const trail = useTrail(rows.length, {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 340, friction: 28 },
  });

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
      {trail.map((style, idx) => (
        <animated.div key={`${rows[idx].stage}-${idx}`} style={style} className="h-full">
          <StepCard row={rows[idx]} index={idx} />
        </animated.div>
      ))}
    </div>
  );
}

function StepCard({ row, index }: { row: StageRow; index: number }) {
  const isRunning = row.status === "running";
  const isDone = row.status === "done";
  const isError = row.status === "error";

  // Left accent border colour
  const accentClass = isRunning
    ? "border-l-[hsl(var(--accent-warm))]"
    : isDone ? "border-l-[hsl(var(--success))]"
    : isError ? "border-l-destructive"
    : "border-l-border/40";

  // Opacity breathing for running — smooth, no scale jitter
  const [spring, api] = useSpring(() => ({ opacity: 1, config: { duration: 1200 } }));
  useEffect(() => {
    if (isRunning) {
      api.start({ to: [{ opacity: 0.55 }, { opacity: 1 }], loop: true, config: { duration: 1200 } });
    } else {
      api.stop();
      api.start({ opacity: 1, config: { duration: 200 } });
    }
  }, [isRunning]);

  return (
    <animated.div
      style={{ opacity: spring.opacity }}
      className={[
        "flex h-full min-h-[96px] flex-col rounded-xl border border-border/60 border-l-4 bg-card p-4",
        accentClass,
      ].join(" ")}
    >
      {/* Header: index + badge */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] text-muted-foreground/40">{index + 1}</span>
        {stageBadge(row.status)}
      </div>

      {/* Stage name — truncated to first segment, 2-line clamp */}
      <p className="flex-1 font-mono text-sm font-semibold leading-snug line-clamp-2">
        {shortStageLabel(row.stage)}
      </p>

      {/* Tool name */}
      {row.tools.length > 0 && (
        <p className="mt-2 truncate font-mono text-[10px] text-muted-foreground/50">
          {row.tools[0]}{row.tools.length > 1 ? ` +${row.tools.length - 1}` : ""}
        </p>
      )}

      {row.error && (
        <p className="mt-1 line-clamp-1 font-mono text-[10px] text-destructive">{row.error}</p>
      )}
    </animated.div>
  );
}

// ── Compact step dot bar (shown when report is streaming) ─────────────────────
function RunningStepBar({ rows, onStop }: { rows: StageRow[]; onStop: () => void }) {
  return (
    <div className="shrink-0 flex items-center gap-3 border-b border-border bg-muted/10 px-5 py-2.5">
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        {rows.map((row, idx) => (
          <span
            key={`${row.stage}-${idx}`}
            title={row.stage}
            className={[
              "h-2 w-2 rounded-full transition-colors",
              row.status === "running"
                ? "animate-ping bg-[hsl(var(--accent-warm))]"
                : row.status === "done"
                  ? "bg-[hsl(var(--success))]"
                  : row.status === "error"
                    ? "bg-destructive"
                    : "bg-muted-foreground/20",
            ].join(" ")}
          />
        ))}
        {rows.length > 0 && (
          <span className="ml-1 font-mono text-[10px] text-muted-foreground">
            {rows.filter((r) => r.status === "done").length}/{rows.length} steps
          </span>
        )}
      </div>
      <Button variant="destructive" size="sm" className="h-6 shrink-0 px-2.5 text-xs" onClick={onStop}>
        <Square className="mr-1 h-2.5 w-2.5" />
        Stop
      </Button>
    </div>
  );
}
